import { useState, useEffect } from "react";
import {
  Grid2,
  Typography,
  Box,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import AddButton from "../../themes/overrides/AddButton";
import SearchField from "../../themes/overrides/SearchField";
import EditIcon from "../../components/icons/EditIcon";
import RemoveIcon from "../../components/icons/RemoveIcon";
import MediaIcon from "../../assets/images/brush.svg";
import LoadingOverlay from "../../components/LoadingOverlay";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { getClients, removeClient } from "../../actions/oauthAction";
import AddClient from "./AddClient";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const OauthClients = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteClient, setSelectedDeleteClient] = useState(null);

  const clients = useSelector((state) => state.oauth.clients);
  const uploadSuccess = useSelector((state) => state.oauth.success);

  const [shouldRefresh, setShouldRefresh] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [visibleApiKeys, setVisibleApiKeys] = useState({});

  const openAddMediaModal = () => {
    setModalOpen(true);
    setEditMode(false);
    setSelectedClient(null);
  };

  useEffect(() => {
    if (shouldRefresh || uploadSuccess) {
      setLoading(true);
      Promise.all([dispatch(getClients())]).finally(() => setLoading(false));

      setShouldRefresh(false);
    }
  }, [shouldRefresh, uploadSuccess, dispatch]);

  const handleEdit = (id) => {
    const clientToEdit = clients.find((client) => client._id === id);

    if (clientToEdit) {
      setSelectedClient(clientToEdit);
      setEditMode(true);
      setModalOpen(true);
    }
  };

  const handleDelete = (clientId) => {
    setSelectedDeleteClient(clientId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const success = await dispatch(removeClient(selectedDeleteClient));
    if (success) {
      setShouldRefresh(true);
      showSnackbar("Client deleted successfully!", "success");
    }
    setDeleteModalOpen(false);
    setSelectedDeleteClient(null);
  };

  const handleSubmitSuccess = () => {
    showSnackbar("Client updated successfully!", "success");
    setModalOpen(false);
    setShouldRefresh(true);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const toggleApiKeyVisibility = (clientId) => {
    setVisibleApiKeys((prev) => ({
      ...prev,
      [clientId]: !prev[clientId],
    }));
  };

  const handleCopyApiKey = (apiKey) => {
    navigator.clipboard.writeText(apiKey);
    showSnackbar("API Key copied to clipboard!", "success");
  };

  const columns = [
    { field: "name", headerName: "Client Name", flex: 1 },
    { field: "redirectUri", headerName: "Redirect URI", flex: 1 },
    {
      field: "apiKey",
      headerName: "API Key",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}
        >
          <Typography variant="body2">
            {visibleApiKeys[params.row._id]
              ? params.row.apiKey
              : "*".repeat(35)}
          </Typography>
          <Tooltip title={visibleApiKeys[params.row._id] ? "Hide" : "Show"}>
            <IconButton
              size="small"
              onClick={() => toggleApiKeyVisibility(params.row._id)}
              aria-label="toggle visibility"
            >
              {visibleApiKeys[params.row._id] ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy API Key">
            <IconButton
              size="small"
              onClick={() => handleCopyApiKey(params.row.apiKey)}
              aria-label="copy"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row._id)}
            aria-label="edit"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleDelete(params.row._id)}
            aria-label="delete"
          >
            <RemoveIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredRows = clients
    ?.filter((client) => client.name?.toLowerCase().includes(searchTerm))
    .map((client, index) => ({
      id: index + 1,
      ...client,
    }));

  if (loading) return <LoadingOverlay />;

  if (!loading) {
    return (
      <>
        <Grid2
          container
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid2
            container
            size={12}
            display="flex"
            justifyContent="space-between"
          >
            <Grid2
              size={{ xs: 12, md: 8 }}
              display="flex"
              alignItems="flex-start"
              sx={{ marginLeft: { xs: 4, md: 0 } }}
            >
              <Box
                component="img"
                sx={{
                  width: { xs: 32, md: 40 },
                  marginRight: { xs: 2, md: 4 },
                }}
                src={MediaIcon}
              />
              <Typography variant={"h1"}>OAuth Clients</Typography>
            </Grid2>
            <Grid2
              size={{ xs: 12, md: 4 }}
              offset={{ xs: 4, md: 0 }}
              display="flex"
              justifyContent="flex-end"
            >
              <AddButton
                label="Add App"
                sx={{ mr: 1 }}
                onClick={openAddMediaModal}
              />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} alignItems="center">
            <Grid2 size={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexGrow: 1,
                }}
              >
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <SearchField
                    placeholder="Search App"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{ flex: 1 }}
                  />
                </Grid2>
              </Box>
            </Grid2>
            <Grid2 size={4}></Grid2>
            <Grid2 size={12}>
              <Paper sx={{ overflowX: "auto" }}>
                <Box
                  sx={{
                    width: "100%",
                    minWidth: { xs: 600, md: "100%" },
                    minHeight: 100,
                    alignContent: "center",
                  }}
                >
                  <DataGrid
                    disableRowSelectionOnClick
                    rows={filteredRows}
                    columns={columns}
                    disableColumnSelector
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 15,
                        },
                      },
                    }}
                    sx={{
                      "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                      },
                      "& .MuiDataGrid-cell": {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                    pageSizeOptions={[15]}
                  />
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
          <AddClient
            isOpen={modalOpen}
            onClose={() => {
              setModalOpen(false);
            }}
            onSubmitSuccess={handleSubmitSuccess}
            editMode={editMode}
            client={selectedClient}
          />
        </Grid2>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          text="Are you sure you want to delete this client?"
          onConfirm={handleConfirmDelete}
          deleteButtonLabel="Delete"
        />

        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }
};

export default OauthClients;
